import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Car, CheckCircle, Copy, Lock, BadgeCheck, Fingerprint, ArrowRight, ShieldAlert,
} from 'lucide-react';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const PHOTO_TYPE_LABEL = { antes: 'Antes', durante: 'Durante', despues: 'Después' };

const PublicPassportPage = () => {
  const { API } = useContext(AuthContext);
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const shareUrl = `${window.location.origin}/car-passport/public/${vehicleId}`;

  useEffect(() => {
    const fetchPassport = async () => {
      try {
        const res = await axios.get(`${API}/car-passport/public/${vehicleId}`);
        setData(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPassport();
  }, [vehicleId, API]);

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC', maximumFractionDigits: 0 }).format(price || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado al portapapeles');
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        toast.success('Enlace copiado al portapapeles');
      } catch {
        toast.error('No se pudo copiar el enlace');
      }
      document.body.removeChild(el);
    }
  };

  const passportHash = () => {
    let hash = 0;
    const str = `${vehicleId}-flix-passport`;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return `0x${Math.abs(hash).toString(16).padStart(8, '0')}a94fe2c1b3d7`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 passport-dark-grid flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error || !data?.vehicle) {
    return (
      <div className="min-h-screen bg-slate-950 passport-dark-grid flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center bg-slate-900 border-slate-800 text-white" data-testid="passport-not-found">
          <ShieldAlert className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Documento no encontrado</h2>
          <p className="text-sm text-slate-400 mb-6">
            Este Car Passport no existe o el enlace es incorrecto.
          </p>
          <Button onClick={() => navigate('/')} className="bg-purple-600 hover:bg-purple-700 rounded-full">
            Ir a Flix
          </Button>
        </Card>
      </div>
    );
  }

  const { vehicle, history, total_services } = data;

  return (
    <div className="min-h-screen bg-slate-950 passport-dark-grid text-white" data-testid="public-passport-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80" style={{ backdropFilter: 'blur(16px)' }}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center space-x-3" data-testid="public-header-logo">
            <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold font-heading">Flix</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 rounded-full">
              <BadgeCheck className="w-3.5 h-3.5" /> Documento público verificado
            </span>
            <Button onClick={() => navigate('/auth')} size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700" data-testid="public-header-cta">
              Crear mi Passport
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-10 max-w-5xl">
        {/* Verification banner */}
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-8 flex items-center justify-between gap-4" data-testid="public-verification-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center shrink-0">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-emerald-300 font-medium text-sm">Historial verificado por Flix · Registro inmutable</p>
              <p className="text-emerald-400/60 text-xs font-mono-data">{passportHash()}</p>
            </div>
          </div>
          <span className="hidden md:flex items-center gap-1.5 text-xs text-slate-400">
            <Lock className="w-3.5 h-3.5" /> Solo lectura
          </span>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Document */}
          <div className="lg:col-span-2">
            <div className="passport-grid rounded-2xl border border-slate-200 text-slate-900 shadow-2xl overflow-hidden" data-testid="public-passport-document">
              {/* Doc header */}
              <div className="bg-purple-600 p-6 text-white">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                      <Car className="w-8 h-8" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <Fingerprint className="w-3.5 h-3.5 text-purple-200" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-purple-200">Car Passport</span>
                      </div>
                      <h1 className="text-2xl font-bold" data-testid="public-passport-vehicle">
                        {vehicle.make} {vehicle.model}
                      </h1>
                      <p className="text-purple-200 text-sm">{vehicle.year} · {vehicle.color}</p>
                    </div>
                  </div>
                  <span className="font-mono-data tracking-widest text-lg bg-white/15 border border-white/20 px-4 py-1.5 rounded-lg">
                    {vehicle.license_plate}
                  </span>
                </div>
              </div>

              {/* Doc body */}
              <div className="p-6">
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">VIN / Chasis</p>
                    <p className="font-mono-data text-xs font-semibold break-all">{vehicle.vin || 'No registrado'}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Kilometraje</p>
                    <p className="font-mono-data text-sm font-semibold">{vehicle.mileage?.toLocaleString() || 0} km</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Servicios registrados</p>
                    <p className="font-mono-data text-sm font-semibold">{total_services}</p>
                  </div>
                </div>

                {/* History */}
                <h2 className="text-sm font-bold uppercase tracking-widest text-purple-700 mb-3">
                  Historial de servicios
                </h2>
                {history && history.length > 0 ? (
                  <div className="space-y-2.5" data-testid="public-passport-history">
                    {history.map((entry) => (
                      <div key={entry.id || `${entry.date}-${entry.service_type}`} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{entry.service_type}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {entry.taller_name} {entry.verified && <BadgeCheck className="w-3 h-3 text-emerald-600 inline ml-1" />}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-mono-data text-xs text-slate-400">{formatDate(entry.date)}</p>
                          <p className="text-sm font-semibold text-slate-700">{formatPrice(entry.price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-4 py-6 text-center" data-testid="public-passport-empty">
                    Aún no hay servicios registrados para este vehículo.
                  </p>
                )}

                {/* Doc footer */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-dashed border-slate-200 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Registro inmutable — verificado por Flix</span>
                  <span className="font-mono-data">DOC Nº CP-{vehicleId.slice(0, 8).toUpperCase()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Side: QR + share + CTA */}
          <div className="space-y-6">
            <Card className="bg-slate-900 border-slate-800 p-6 text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Compartir documento</p>
              <div className="bg-white rounded-xl p-4 inline-block mb-4">
                <QRCode value={shareUrl} size={140} data-testid="public-passport-qr" />
              </div>
              <Button
                onClick={copyLink}
                variant="outline"
                className="w-full border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar enlace
              </Button>
              <p className="text-[11px] text-slate-500 mt-3">
                Cualquier persona con este enlace puede verificar el historial.
              </p>
            </Card>

            <Card className="bg-purple-600 border-purple-500 p-6 text-white">
              <h3 className="text-lg font-bold mb-2">¿Vendes o compras un vehículo?</h3>
              <p className="text-sm text-purple-100 mb-4 leading-relaxed">
                Un Car Passport con historial certificado aumenta el valor de reventa hasta un 15%.
              </p>
              <Button
                onClick={() => navigate('/auth')}
                className="w-full rounded-full bg-white text-purple-700 hover:bg-purple-50 active:scale-95 transition-transform"
                data-testid="public-cta-btn"
              >
                Crear mi Car Passport gratis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-8">
        <div className="container mx-auto px-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <Lock className="w-3.5 h-3.5" />
          Documento generado por Flix · {new Date().getFullYear()} · Todos los derechos reservados
        </div>
      </footer>
    </div>
  );
};

export default PublicPassportPage;
